import { clearParentBrowserState } from './parentBrowserState';
import { resetImageIntentSession } from './imageIntentReporter';

// redux-persist's root key (packs/index.js persistConfig).
const PERSIST_ROOT_KEY = 'persist:root';
// Which support session this TAB last rendered. Deliberately sessionStorage:
// localStorage is shared by every tab on the origin, so two support tabs would
// overwrite each other's marker and each could then skip the purge and load the
// other family's state.
const MARKER_KEY = 'pics_support_session';

// The live support session's id, or '' for an ordinary parent visit. Read from
// the layout rather than the store, because it decides how the store is built.
export const readSupportSessionId = (): string => {
  try {
    return (
      document.querySelector('#main')?.getAttribute('data-support-session') ||
      ''
    );
  } catch {
    return '';
  }
};

const removeKey = (store: Storage | undefined, key: string): void => {
  try {
    store?.removeItem(key);
  } catch {
    /* storage unavailable -- nothing to clear */
  }
};

// Everything one family's visit leaves behind: the persisted Redux root (cart,
// selected student, gallery and shoot slices) in both storages, the gallery keys
// and entry-point cookies that live outside Redux, and the image-intent tab
// session.
//
// Deliberately the same list logoutAndReset clears, minus the logout request
// itself -- a support session ends through its own endpoint.
export const purgeParentSession = (): void => {
  removeKey(window.localStorage, PERSIST_ROOT_KEY);
  removeKey(window.sessionStorage, PERSIST_ROOT_KEY);

  clearParentBrowserState();
  resetImageIntentSession();
};

// Runs before the store is created, so a changed session rehydrates nothing.
//
// Clearing the Rails session cannot touch browser storage, so without this an
// admin who views as one family and then another gets the first family's cart
// and galleries rehydrated on the second family's dashboard. The transition out
// of a session matters just as much: Exit, expiry and a closed tab all land
// here with no id, and must leave nothing behind for the next person.
export const syncSupportSession = (): void => {
  const current = readSupportSessionId();

  let previous = '';
  try {
    previous = window.sessionStorage.getItem(MARKER_KEY) || '';
  } catch {
    return;
  }

  if (current === previous) return;

  purgeParentSession();

  try {
    if (current) {
      window.sessionStorage.setItem(MARKER_KEY, current);
    } else {
      window.sessionStorage.removeItem(MARKER_KEY);
    }
  } catch {
    /* nothing to record against */
  }
};

export default syncSupportSession;
